/**
 * PHARMAKON — Fournisseur ERP · RequireSupplierRole guard (Module 00, E.11)
 *
 * Route-level guard. Redirects unauthenticated users to /login. Authenticated
 * users without a supplier role (or without the optional capability) see a
 * French 403 panel with a recovery action — never a blank page.
 *
 * `roles`     — optional explicit allow-list (defaults to all supplier roles).
 * `capability`— optional client-side capability key (server still enforces).
 */
import * as React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore.js";
import { Icon, Button } from "@/components/ui/primitives";
import {
  isSupplierRole,
  SUPPLIER_ROLES,
  supplierCan,
} from "@/config/fournisseur.constants.js";

function ForbiddenPanel({ title, description }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center text-center px-6 py-24 min-h-[60vh]">
      <div className="w-14 h-14 rounded-full bg-danger-soft text-danger flex items-center justify-center mb-4">
        <Icon name="shield-alert" size={24} />
      </div>
      <h2 className="text-lg font-semibold text-text-strong mb-1">{title}</h2>
      <p className="text-sm text-text-muted max-w-md mb-5">{description}</p>
      <Button
        variant="secondary"
        icon="arrow-left"
        onClick={() => navigate("/portail-fournisseur/dashboard")}
      >
        Retour au tableau de bord
      </Button>
    </div>
  );
}

export default function RequireSupplierRole({
  roles = SUPPLIER_ROLES,
  capability,
  children,
}) {
  const user = useAuthStore((s) => s.user);
  const loc = useLocation();

  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;

  // Must hold a supplier role at all.
  if (!isSupplierRole(user.role)) {
    return (
      <ForbiddenPanel
        title="Accès réservé aux fournisseurs"
        description="Ce portail est réservé aux comptes fournisseur et laboratoire. Connectez-vous avec un compte autorisé."
      />
    );
  }

  // Explicit role allow-list (when narrower than all supplier roles).
  if (roles && !roles.includes(user.role)) {
    return (
      <ForbiddenPanel
        title="Permission insuffisante"
        description="Votre rôle ne vous permet pas d'accéder à cette section. Contactez l'administrateur de votre organisation."
      />
    );
  }

  // Optional capability check (UI mirror of the server matrix).
  if (capability && !supplierCan(user.role, capability)) {
    return (
      <ForbiddenPanel
        title="Permission insuffisante"
        description="Votre rôle ne dispose pas des droits requis pour cette action. Contactez l'administrateur de votre organisation."
      />
    );
  }

  return <>{children}</>;
}
